import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import {
  JobCatalogTreeFragment,
  UpdateContractsContractVersionFlowDocument,
} from 'generated/resources/graphql'
import { IObservableArray } from 'mobx'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { ValidationError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import Dates from 'modules/contracts/components/FlowPage/Steps/Dates'
import LegalAndExtras from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import WorkHours from 'modules/contracts/components/FlowPage/Steps/WorkHours'
import { useNewAdditionalCompensationsFeatureEnabled } from 'modules/contracts/hooks/useNewAdditionalCompensationsFeatureEnabled'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getCustomFieldKey } from 'modules/contracts/lib/customFields'
import { isGraphQLType } from 'modules/contracts/lib/types'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { FlowPageContent } from '../FlowPage/_FlowPageContent'
import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import LegalAndExtrasStep from './lib/LegalAndExtrasStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import WorkHoursStep from './lib/WorkHoursStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { translations as legalAndExtrasTranslations } from './lib/legalAndExtrasTranslations'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'
import { getUpdateFlowForm } from './lib/updateFlowForm'
import { translations as workingHoursTranslations } from './lib/workingHoursTranslations'

interface Props {
  customFields: ResourceField[]
  customValues: IObservableArray<CustomFieldValue>
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
}

const UpdateFlowContent = ({
  customFields,
  customValues,
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    employee,
    contractVersion,
    compensations,
    contractVersionPolicy,
    legalEntityContractData,
    additionalCompensations,
  } = useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const canSeeWorkingHours: boolean = policyAuthorizations.for(
    'contracts.see_working_hours',
    employee
  )
  const form = useLazyRef(() =>
    getUpdateFlowForm(
      contractVersion,
      employee,
      contractVersionPolicy,
      customFields,
      customValues,
      policyAuthorizations
    )
  ).current
  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: employee.id.toString(),
      })
    )
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const flowSteps = [
    new DatesStep(
      (
        <Dates
          step={1}
          type="update"
          form={form}
          translations={datesStepTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        />
      )
    ),
    new RoleLevelSalaryStep(
      (
        <RoleLevelSalary
          step={2}
          type="update"
          form={form}
          canEditPayrollPolicy
          translations={roleLevelSalaryTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
        />
      )
    ),
  ]

  // Avoid showing a blank step when hours permissions are disabled
  if (canSeeWorkingHours) {
    flowSteps.push(
      new WorkHoursStep(
        (
          <WorkHours
            step={3}
            type="update"
            form={form}
            translations={workingHoursTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            legalEntity={employeeLegalEntity(employee)}
          />
        )
      )
    )
  }
  flowSteps.push(
    new LegalAndExtrasStep(
      (
        <LegalAndExtras
          step={canSeeWorkingHours ? 4 : 3}
          type="update"
          form={form}
          translations={legalAndExtrasTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        />
      )
    )
  )
  const updateContractFlow = useLazyRef(() => new ContractFlow(flowSteps)).current

  const getEditedCustomFields = (customFields: ResourceField[]) => {
    const editedCustomFields: { [key: string]: string }[] = []

    return customFields.reduce((editedCustomFields, field) => {
      const key = getCustomFieldKey(field)
      const cf_field = form.get(key)
      if (cf_field.isDirty) {
        const attributes = field.attributes
        editedCustomFields.push({
          identifier: attributes.identifier,
          value: cf_field._mapOut(),
        })
      }
      return editedCustomFields
    }, editedCustomFields)
  }

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const { company } = useCurrentSession()
  const isNewAdditionalCompensationsSupportEnabled = useNewAdditionalCompensationsFeatureEnabled({
    companyId: company.id,
  })

  const [updateContractsContractVersionFlow] = useMutation(
    UpdateContractsContractVersionFlowDocument
  )

  const onSubmit = () => {
    setIsSubmitting(true)
    const api = ApiClient()
    form.get('payroll_policy_id').set(contractVersionPolicy?.attributes.payroll_policy_id)
    const data = form.serialized
    data.additional_compensations = compensations.map((compensation) => compensation.attributes)
    data.custom_fields = getEditedCustomFields(customFields)
    data.employee_id = employee.id

    if (isNewAdditionalCompensationsSupportEnabled) {
      return updateContractsContractVersionFlow({
        variables: {
          additionalCompensations: data.additional_compensations,
          newAdditionalCompensations: additionalCompensations.map(({ tmpID: _, ...rest }) => {
            return {
              amount: rest.amountStrategy?.amount,
              amountStrategyType: rest.amountStrategyType,
              contractVersionId: rest.contractVersionId,
              description: rest.description,
              firstPaymentOn: rest.firstPaymentOn,
              payrollConceptId: rest.payrollConceptId,
              recurrence: rest.recurrence,
              upperLimit:
                rest.amountStrategy &&
                isGraphQLType(rest.amountStrategy, 'ContractsVariableAmountStrategy')
                  ? rest.amountStrategy?.upperLimit
                  : 0,
            }
          }),
          customFields: data.custom_fields,
          deActivityType: data.de_activity_type,
          deContractTypeId: data.de_contract_type_id,
          deEmploymentType: data.de_employment_type,
          deFlatRateTax: data.de_flat_rate_tax,
          dePersonalKeyGroupId: data.de_personal_key_group_id,
          effectiveOn: data.effective_on,
          employeeId: employee.id,
          endsOn: data.ends_on,
          esA3innuvaJobPositionPayrollIntegrationCode:
            data.es_a3innuva_job_position_payroll_integration_code,
          esA3innuvaJobPositionPayrollIntegrationName:
            data.es_a3innuva_job_position_payroll_integration_name,
          esAgreementCodePayrollIntegrationCode: data.es_agreement_code_payroll_integration_code,
          esAgreementCodePayrollIntegrationName: data.es_agreement_code_payroll_integration_name,
          esClassificationPayrollIntegrationCode: data.es_classification_payroll_integration_code,
          esClassificationPayrollIntegrationName: data.es_classification_payroll_integration_name,
          esCnoOccupationPayrollIntegrationCode: data.es_cno_occupation_payroll_integration_code,
          esCnoOccupationPayrollIntegrationName: data.es_cno_occupation_payroll_integration_name,
          esContractObservations: data.es_contract_observations,
          esContractTypeId: data.es_contract_type_id,
          esContractTypePayrollIntegrationCode: data.es_contract_type_payroll_integration_code,
          esContractTypePayrollIntegrationName: data.es_contract_type_payroll_integration_name,
          esContributionTypePayrollIntegrationCode:
            data.es_contribution_type_payroll_integration_code,
          esContributionTypePayrollIntegrationName:
            data.es_contribution_type_payroll_integration_name,
          esCotizationGroup: data.es_cotization_group,
          esEducationLevelId: data.es_education_level_id,
          esEducationLevelPayrollIntegrationCode: data.es_education_level_payroll_integration_code,
          esEducationLevelPayrollIntegrationName: data.es_education_level_payroll_integration_name,
          esHasTeleworkingContract: data.es_has_teleworking_contract,
          esJobDescription: data.es_job_description,
          esOccupationCodePayrollIntegrationCode: data.es_occupation_code_payroll_integration_code,
          esOccupationCodePayrollIntegrationName: data.es_occupation_code_payroll_integration_name,
          esProfessionalCategoryId: data.es_professional_category_id,
          esRegimePayrollIntegrationCode: data.es_regime_payroll_integration_code,
          esRegimePayrollIntegrationName: data.es_regime_payroll_integration_name,
          esTariffGroupId: data.es_tariff_group_id ? String(data.es_tariff_group_id) : '',
          esTariffGroupName: data.es_tariff_group_name,
          esWorkingDayTypeId: data.es_working_day_type_id,
          frCoefficient: data.fr_coefficient,
          frContractTypeId: data.fr_contract_type_id,
          frEmployeeType: data.fr_employee_type,
          frForfaitJours: data.fr_forfait_jours,
          frJoursParAn: data.fr_jours_par_an,
          frJoursParAnCents: data.fr_jours_par_an_cents,
          frLevelId: data.fr_level_id,
          frMutualId: data.fr_mutual_id,
          frProfessionalCategoryId: data.fr_professional_category_id,
          frStepId: data.fr_step_id,
          frWorkTypeId: data.fr_work_type_id,
          hasTrialPeriod: data.has_trial_period,
          jobCatalogLevelId: data.job_catalog_level_id,
          jobCatalogRoleId: data.job_catalog_role_id,
          jobTitle: data.job_title,
          maxLegalYearlyHours: data.max_legal_yearly_hours,
          maximumWeeklyHours: data.maximum_weekly_hours,
          payrollPolicyId: data.payroll_policy_id,
          ptContractTypeId: data.pt_contract_type_id,
          salaryAmount: data.salary_amount,
          salaryFrequency: data.salary_frequency,
          startsOn: data.starts_on,
          trialPeriodEndsOn: data.trial_period_ends_on,
          workingHours: data.working_hours,
          workingHoursFrequency: data.working_hours_frequency,
          workingWeekDays: data.working_week_days,
        },
        onCompleted: (data) => {
          const contractVersionId =
            data.contractsMutations.updateContractsContractVersionFlow.contractVersionFlow
              ?.contractVersionId &&
            Number(
              data.contractsMutations.updateContractsContractVersionFlow.contractVersionFlow
                ?.contractVersionId
            )
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.update.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          contractVersionId &&
            navigate(
              navigation.employees.id.contractVersions.contractVersionId.fullPath({
                id: employee.id.toString(),
                contractVersionId,
              })
            )
        },
        onError: (error) => {
          showFlashMessage('wrong', error.message)
          setIsSubmitting(false)
        },
      })
    } else {
      api
        .post(`/contracts/flows/update`, data)
        .then((newContract) => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.update.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          navigate(
            navigation.employees.id.contractVersions.contractVersionId.fullPath({
              id: employee.id.toString(),
              contractVersionId: newContract.contract_version_id.toString(),
            })
          )
        })
        .catch((e) => {
          if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
          else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        })
    }
  }

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      employee={employee}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.update.header.title', {
        employeeFullName: employeeUser(employee).fullName(),
      })}
      flow={updateContractFlow}
      footerContent={<Footer flow={updateContractFlow} employee={employee} />}
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
    />
  )
}

export default observer(UpdateFlowContent)
